import React from "react";
import { Clock, X, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PollCreatorService } from "../../services/PollCreatorService";

// Types
interface TimeSlot {
  hour: number;
  minute: number;
  label: string;
  enabled: boolean;
}

export interface GranularitySelectorProps {
  timeGranularity: number;
  timeSlots: TimeSlot[];
  timeSlotsByDate: Record<string, TimeSlot[]>;
  showGranularitySettings: boolean;
  onGranularityChange: (value: number) => void;
  onToggleSettings: () => void;
  onUndoChange: () => void;
  className?: string;
}

export const GranularitySelector: React.FC<GranularitySelectorProps> = ({
  timeGranularity,
  timeSlots,
  timeSlotsByDate,
  showGranularitySettings,
  onGranularityChange,
  onToggleSettings,
  onUndoChange,
  className = "",
}) => {
  const { t } = useTranslation("polls");
  const granularityOptions = [
    { value: 15, label: "15 min" },
    { value: 30, label: "30 min" },
    { value: 60, label: t("granularity.option.hour", { count: 1 }) },
    { value: 120, label: t("granularity.option.hours", { count: 2 }) },
    { value: 240, label: t("granularity.option.hours", { count: 4 }) },
  ];

  // Check if there are active time slots selected
  const hasActiveSlots = (() => {
    if (!timeSlotsByDate) return false;

    const selectedSlots = Object.values(timeSlotsByDate).flat();
    const enabledSlots = selectedSlots.filter((slot) => slot.enabled);

    return enabledSlots.length > 0;
  })();

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={onToggleSettings}
        className={`flex items-center gap-2 px-3 py-2 text-sm sm:px-4 sm:text-base border border-gray-300 rounded-lg hover:border-blue-300 transition-colors whitespace-nowrap ${
          showGranularitySettings ? "border-blue-300" : ""
        }`}
      >
        <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
        <span>{t("granularity.button")}</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${
            showGranularitySettings ? "rotate-180" : ""
          }`}
        />
      </button>

      {showGranularitySettings && (
        <div className="absolute top-full left-0 mt-2 w-[min(20rem,calc(100vw-2rem))] bg-[#1e1e1e] border border-gray-700 rounded-lg shadow-lg z-50 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-100">
              {t("granularity.title")}
            </h3>
            <button
              onClick={() => {
                // Fermer les paramètres
                onToggleSettings();
              }}
              className="text-gray-400 hover:text-gray-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {hasActiveSlots && (
            <div className="mb-4 p-3 bg-yellow-900/20 border border-yellow-600/50 rounded-lg">
              <p className="text-sm text-yellow-300">
                {t("granularity.warning")}
              </p>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            {granularityOptions.map((option) => {
              const compatible = (() => {
                // TOUJOURS autoriser le changement de granularité
                return true;
              })();
              return (
                <button
                  key={option.value}
                  onClick={() =>
                    compatible && onGranularityChange(option.value)
                  }
                  disabled={!compatible}
                  className={`px-3 py-1 text-sm rounded-full transition-colors
                    ${
                      timeGranularity === option.value
                        ? "bg-blue-500 text-white"
                        : compatible
                          ? "bg-[#1e1e1e] border border-gray-700 hover:border-blue-500 text-white"
                          : "bg-[#0a0a0a] border border-gray-800 text-gray-600 cursor-not-allowed"
                    }
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          {PollCreatorService.initialGranularityState && (
            <button
              onClick={() => {
                // TODO: Implémenter undoGranularityChange avec setState correct
                onUndoChange();
              }}
              className="mt-3 text-sm text-gray-600 hover:text-gray-800"
            >
              {t("granularity.undo")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
